import { computed, type ComputedRef } from 'vue'
import { usePipe, isInPipeContext } from '@render/composables/usePipe'
import {
  buildLocaleConfig,
  resolveLocale,
  formatNumber as formatNumberRaw,
  formatCurrencyShort as formatCurrencyShortRaw,
  formatCurrencyShortTrim as formatCurrencyShortTrimRaw,
  formatCurrencyLong as formatCurrencyLongRaw,
  type LocaleConfig,
  type MeasurementSystem,
  type ResolvedLocaleOverride,
} from '@render/lib/localeConfig'
import {
  formatHeightWeight as formatHeightWeightRaw,
  type HeightWeightFormat,
} from '@render/lib/interpolationFormat'

/**
 * Reactive effective locale config for the current pipe.
 *
 * Reads `const.system.{language,geography,currency}` + the resolved override
 * carrier `const.system.localeConfig`, then resolves locale → derives defaults
 * → merges the override. Works in built pipes (initialize-fed), the editor
 * preview iframe (postMessage-fed) and — when called outside a pipe context —
 * degrades to plain `en`/USD defaults so editor-only consumers (e.g. the
 * interpolation-format modal host) can still build a config.
 *
 * SSR-safe: pure reads of constants + `Intl`, no `window`/`onMounted`.
 */
export function useLocale() {
  const pipe = isInPipeContext() ? usePipe() : null

  const language = computed(
    () => (pipe?.getData('const.system.language') as string | undefined) || 'en',
  )
  const geography = computed(
    () => (pipe?.getData('const.system.geography') as string | undefined) || '',
  )
  const currency = computed(() =>
    ((pipe?.getData('const.system.currency') as string | undefined) || 'usd').toUpperCase(),
  )
  const override = computed(
    () => pipe?.getData('const.system.localeConfig') as ResolvedLocaleOverride | undefined,
  )

  const localeString = computed(() => resolveLocale(language.value, geography.value))
  const config = computed<LocaleConfig>(() =>
    buildLocaleConfig(language.value, geography.value, currency.value, override.value),
  )

  /** Locale default for metric/imperial (overridable per locale). */
  const defaultMeasurementSystem = computed<MeasurementSystem>(() => config.value.measurementSystem)
  /** Effective measurement system: explicit user/runtime var wins, else locale default. */
  const measurementSystem = computed<MeasurementSystem>(() => {
    const raw = pipe?.getData('var.global.measurementSystem')
    if (raw === 'imperial' || raw === 'metric') return raw
    return defaultMeasurementSystem.value
  })

  return {
    language,
    geography,
    currency,
    override,
    localeString,
    config,
    defaultMeasurementSystem,
    measurementSystem,
    formatNumber: (value: unknown, decimals?: number | null) =>
      formatNumberRaw(value, config.value, decimals),
    formatCurrencyShort: (value: unknown) => formatCurrencyShortRaw(value, config.value),
    /** Symbol form with a zero fraction dropped — `$1.00` → `$1`, `$13.50` kept. */
    formatCurrencyShortTrim: (value: unknown) => formatCurrencyShortTrimRaw(value, config.value),
    formatCurrencyLong: (value: unknown) => formatCurrencyLongRaw(value, config.value),
    formatHeightWeight: (
      value: unknown,
      format: HeightWeightFormat,
      system: MeasurementSystem,
      decimals?: number | null,
    ) => formatHeightWeightRaw(value, format, system, config.value, decimals),
  }
}

export type UseLocaleReturn = ReturnType<typeof useLocale>
export type { LocaleConfig, MeasurementSystem }
export type LocaleConfigRef = ComputedRef<LocaleConfig>
